import CryptoJS from 'crypto-js';

export class AESEncryptionUtil {
  private static readonly IV_LENGTH = 16; // 16 bytes for CBC
  private static readonly SALT_LENGTH = 16;
  private static readonly ITERATIONS = 65536;

  // Encrypt data using AES (CBC mode, NoPadding)
  static encryptPayload(data: any, password: string): string {
    const salt = CryptoJS.lib.WordArray.random(this.SALT_LENGTH);
    const iv = CryptoJS.lib.WordArray.random(this.IV_LENGTH);

    let dataStr = typeof data === 'string' ? data : JSON.stringify(data);

    // Adjust data to be a multiple of 16 bytes (AES block size)
    const blockSize = 16;
    const paddingLength = blockSize - (dataStr.length % blockSize);
    if (paddingLength !== blockSize) {
      const paddedData = dataStr + ' '.repeat(paddingLength); // Padding with spaces
      dataStr = paddedData;
    }

    const key = CryptoJS.PBKDF2(password, salt, {
      keySize: 256 / 32,
      iterations: this.ITERATIONS,
      hasher: CryptoJS.algo.SHA256,
    });

    const encrypted = CryptoJS.AES.encrypt(dataStr, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC, // AES CBC mode
      padding: CryptoJS.pad.NoPadding, // No padding
    });

    const saltStr = CryptoJS.enc.Base64.stringify(salt);
    const ivStr = CryptoJS.enc.Base64.stringify(iv);
    const encryptedStr = encrypted.toString();

    return `${saltStr}:${ivStr}:${encryptedStr}`;
  }

  // Decrypt encrypted data using AES (CBC mode, NoPadding)
  static decryptPayload(encryptedData: string, password: string): string {
    const parts = encryptedData.split(':');
    if (parts.length !== 3) {
      throw new Error('Invalid encrypted data format');
    }

    const salt = CryptoJS.enc.Base64.parse(parts[0]);
    const iv = CryptoJS.enc.Base64.parse(parts[1]);
    const encryptedStr = parts[2];

    const key = CryptoJS.PBKDF2(password, salt, {
      keySize: 256 / 32,
      iterations: this.ITERATIONS,
      hasher: CryptoJS.algo.SHA256,
    });

    const decrypted = CryptoJS.AES.decrypt(encryptedStr, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC, // AES CBC mode
      padding: CryptoJS.pad.NoPadding, // No padding
    });

    let decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);

    // Remove padding (spaces) added during encryption
    const paddingLength = decryptedStr.length % 16;
    if (paddingLength !== 0) {
      decryptedStr = decryptedStr.substring(
        0,
        decryptedStr.length - paddingLength
      );
    }

    return decryptedStr;
  }
}
